import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Fingerprint, CheckCircle2, ShieldCheck, Zap, Wallet } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { scrollToId } from "./SmoothScroll";

const ease = [0.22, 1, 0.36, 1];

const TerminalCard = ({ c }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1, duration: 1, ease }}
      className="terminal-card w-[270px] sm:w-[300px]"
      data-testid="hero-aeps-card"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">{c.title}</span>
        <span className="h-2 w-2 animate-pulse rounded-full bg-emerald" />
      </div>
      <div className="mt-5 flex items-center gap-4" style={{ transform: "translateZ(40px)" }}>
        <div className="scan-ring">
          <Fingerprint size={30} className="text-saffron" />
        </div>
        <div>
          <p className="text-xs text-slate-400">{c.bank}</p>
          <p className="font-mono text-3xl font-semibold text-white">{c.amount}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-2 rounded-xl bg-emerald/15 px-3 py-2.5 text-sm text-emerald-300">
        <CheckCircle2 size={16} /> {c.status}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
        <span>{c.comm}</span>
        <span className="font-mono text-saffron">+ ₹ ••</span>
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pb-20 pt-32 lg:pb-28 lg:pt-40" data-testid="hero-section">
      <div className="hero-glow" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="eyebrow">
            <span className="eyebrow-dot" />{h.over}
          </motion.p>
          <h1 key={lang} className="hero-title mt-6" data-testid="hero-title">
            {h.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span
                  className={`block ${i === 2 ? "text-saffron italic" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.15 + i * 0.14, duration: 1.1, ease }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9, ease }} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-testid="hero-subtitle">
            {h.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease }} className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("join")} className="btn-primary btn-lg group" data-testid="hero-join-btn">
              {h.cta1} <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToId("calculator")} className="btn-ghost btn-lg" data-testid="hero-calc-btn">{h.cta2}</button>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }} className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300">
            {[[Wallet, h.chip1], [Zap, h.chip2], [ShieldCheck, h.chip3]].map(([I, s]) => (
              <li key={s} className="flex items-center gap-2"><I size={16} className="text-emerald" />{s}</li>
            ))}
          </motion.ul>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
            transition={{ delay: 0.3, duration: 1.4, ease }}
            className="hero-frame"
          >
            <motion.img src="/img/shop1.jpg" alt="Indian shopkeeper at his store" style={{ y: imgY, scale: imgScale }} className="h-full w-full object-cover" data-testid="hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          </motion.div>
          <div className="relative z-10 -mt-16 flex justify-center sm:absolute sm:-bottom-10 sm:-left-10 sm:mt-0 lg:-left-16">
            <TerminalCard c={h.card} />
          </div>
        </div>
      </div>
    </section>
  );
};
